import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { teamsService } from "@/services/teamsService";
import { useAuth } from "@/contexts/AuthContext";
import { useUser } from "@/contexts/UserContext";

// Update the InviteInfo type to match the fields returned by the API
type InviteInfo = {
  isValid: boolean;
  teamId: string | null;
  teamName: string | null;
  expiresAt: string | null;
};

export default function InvitePage() {
  const { isAuthenticated, user: authUser } = useAuth();
  const { updateUserProfile } = useUser();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("code");
  const navigate = useNavigate();
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if the invitation is valid
  useEffect(() => {
    if (!inviteCode) {
      setIsLoading(false);
      return;
    }

    const checkInvitation = async () => {
      try {
        setIsLoading(true);
        const data = await teamsService.validateInvitation(inviteCode);

        if (data) {
          setInviteInfo({
            isValid: data.isValid,
            teamId: data.teamId,
            teamName: data.teamName,
            expiresAt: data.expiresAt,
          });
        } else {
          setInviteInfo({
            isValid: false,
            teamId: null,
            teamName: null,
            expiresAt: null,
          });
        }
      } catch (error: any) {
        console.error("Error checking invitation:", error);
        toast.error("Invalid invitation link");
        setInviteInfo({
          isValid: false,
          teamId: null,
          teamName: null,
          expiresAt: null,
        });
      } finally {
        setIsLoading(false);
      }
    };

    checkInvitation();
  }, [inviteCode]);

  // If user is authenticated and has a valid invitation, redirect to onboarding
  useEffect(() => {
    if (isAuthenticated && inviteInfo?.isValid && inviteCode && authUser?.id) {
      // Store invitation information in localStorage for onboarding
      const invitationData = {
        inviteCode,
        teamId: inviteInfo.teamId,
        teamName: inviteInfo.teamName,
        userId: authUser.id,
        timestamp: Date.now(), // Add timestamp for cleanup
      };

      localStorage.setItem("invitationData", JSON.stringify(invitationData));

      // Also store in sessionStorage as backup
      sessionStorage.setItem("invitationData", JSON.stringify(invitationData));

      toast.success(`Welcome! You're joining ${inviteInfo.teamName}`);

      // Redirect to onboarding
      navigate("/");
    }
  }, [isAuthenticated, inviteInfo, inviteCode, navigate, authUser]);

  // Handle invalid or expired invitation
  if (!isLoading && (!inviteInfo || !inviteInfo.isValid)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md mx-auto shadow-md">
          <CardHeader>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>
              This invitation link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate("/")} className="w-full">
              Go to Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // If user is not signed in, show signup prompt
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md mx-auto shadow-md">
          <CardHeader>
            <CardTitle>Join {inviteInfo?.teamName || "Team"}</CardTitle>
            <CardDescription>
              You've been invited to join {inviteInfo?.teamName || "a team"}.
              Please sign up or log in to continue.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={() => navigate(`/?signup=true&inviteCode=${inviteCode}`)} className="w-full">
              Create Account
            </Button>
            <Button variant="outline" onClick={() => navigate(`/?inviteCode=${inviteCode}`)} className="w-full">
              Log In
            </Button>
          </CardContent>
          <CardFooter>
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="w-full"
            >
              Cancel
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // If user is authenticated, show redirect message
  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-md mx-auto shadow-md">
        <CardHeader>
          <CardTitle>Redirecting...</CardTitle>
          <CardDescription>
            Setting up your account for {inviteInfo?.teamName}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
        </CardContent>
      </Card>
    </div>
  );
}
